import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { hashIdentifier } from '../lib/hash';
import { fetchNIPRLicenses } from '../lib/nipr';
import { capitalizeName } from '../lib/format';

// ─── STAR PICKER ─────────────────────────────────────────────────────────────
export const StarPicker = ({ value, onChange, size = 32 }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div style={{ display: 'flex', gap: '6px', cursor: 'pointer' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <svg
          key={star}
          width={size}
          height={size}
          viewBox="0 0 20 20"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          style={{ transition: 'transform 0.1s' }}
        >
          <polygon
            points="10,1 12.9,7 19.5,7.6 14.5,12 16.2,18.5 10,15 3.8,18.5 5.5,12 0.5,7.6 7.1,7"
            fill={(hovered || value) >= star ? 'var(--gold)' : 'var(--border)'}
            stroke="var(--gold)"
            strokeWidth="0.5"
          />
        </svg>
      ))}
    </div>
  );
};

// ─── RATING LABELS ───────────────────────────────────────────────────────────
const RATING_LABELS = {
  1: 'Poor',
  2: 'Below Average',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

// ─── RATE MODAL ──────────────────────────────────────────────────────────────
export const RateModal = ({ profile, currentUserId, userRole, onClose, onRated }) => {
  const [rating,    setRating]    = useState(0);
  const [existing,  setExisting]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [submitting,setSubmitting]= useState(false);
  const [error,     setError]     = useState('');
  const [agreed,    setAgreed]    = useState(false);
  const [done,      setDone]      = useState(false);

  // Check for existing rating
  useEffect(() => {
    const check = async () => {
      if (!currentUserId || !profile?.id) { setLoading(false); return; }
      const { data } = await supabase
        .from('ratings')
        .select('id, rating')
        .eq('rated_profile_id', profile.id)
        .eq('rater_user_id', currentUserId)
        .single();
      if (data) { setExisting(data); setRating(data.rating); }
      setLoading(false);
    };
    check();
  }, [currentUserId, profile?.user_id]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!rating)  { setError('Please select a star rating'); return; }
    if (!agreed)  { setError('Please confirm the attestation'); return; }
    setSubmitting(true);
    setError('');

    try {
      const { error: upsertError } = await supabase
        .from('ratings')
        .upsert(
          {
            rated_profile_id: profile.id,
            rater_user_id: currentUserId,
            rating,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'rated_profile_id,rater_user_id' }
        );
      if (upsertError) throw new Error(upsertError.message);

      // Update profile rating aggregates
      await updateProfileRatings(profile.id, userRole);

      setDone(true);
      if (onRated) onRated();
      setTimeout(onClose, 1500);

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '480px', padding: '32px', boxShadow: 'var(--shadow-lg)' }}>

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: 'var(--navy)', marginBottom: '8px' }}>
              {existing ? 'Rating updated' : 'Rating submitted'}
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: '14px' }}>Thank you for your feedback.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--gold)', marginBottom: '4px' }}>
                  {existing ? 'Update your rating' : 'Rate this umpire'}
                </div>
                <h3 style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600 }}>
                  {capitalizeName(profile?.display_name)}
                </h3>
              </div>
              <button onClick={onClose} style={{ background: 'var(--cream)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '18px', color: 'var(--muted)' }}>×</button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>Loading...</div>
            ) : (
              <>
                {/* Star picker */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px', fontWeight: 300 }}>
                    Select your rating:
                  </div>
                  <StarPicker value={rating} onChange={setRating} size={36} />
                  {rating > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--gold)', fontWeight: 500 }}>
                      {RATING_LABELS[rating]}
                    </div>
                  )}
                </div>

                {/* Attestation */}
                <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold)', marginBottom: '8px' }}>
                    ⚠ Before you rate
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, marginBottom: '12px' }}>
                    Ratings must be based on your direct professional experience. Stick to facts and honest professional opinion.
                    Defamatory or knowingly false statements expose <strong>you personally</strong> to civil liability —
                    the platform is not responsible for what you submit.
                  </p>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                      style={{ marginTop: '2px', accentColor: 'var(--gold)' }}
                    />
                    <span style={{ fontSize: '12px', color: 'var(--navy)', fontWeight: 500, lineHeight: 1.5 }}>
                      This rating reflects my genuine professional experience with this umpire.
                    </span>
                  </label>
                </div>

                {error && (
                  <div style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#c0392b', marginBottom: '16px' }}>
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={onClose}
                    style={{ flex: 1, padding: '12px', background: 'transparent', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--muted)', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !rating || !agreed}
                    style={{ flex: 2, padding: '12px', background: 'var(--navy)', color: 'var(--white)', border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 500, cursor: 'pointer', opacity: (submitting || !rating || !agreed) ? 0.5 : 1 }}
                  >
                    {submitting ? 'Submitting...' : existing ? 'Update Rating' : 'Submit Rating'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── UPDATE PROFILE RATING AGGREGATES ────────────────────────────────────────
export const updateProfileRatings = async (profileId, raterRole) => {
  const { data: allRatings } = await supabase
    .from('ratings')
    .select('rating, rater_user_id')
    .eq('rated_profile_id', profileId);

  if (!allRatings || allRatings.length === 0) return;

  const raterIds = allRatings.map(r => r.rater_user_id);
  const { data: raterLicenses } = await supabase
    .rpc('get_public_license_info', { p_user_ids: raterIds });

  const licenseMap = {};
  raterLicenses?.forEach(l => { licenseMap[l.user_id] = l.license_type; });

  const iaRatings = allRatings.filter(r => licenseMap[r.rater_user_id] === 'IA');
  const paRatings = allRatings.filter(r => licenseMap[r.rater_user_id] === 'PA');

  const avg = (arr) => arr.length > 0
    ? Math.round((arr.reduce((s, r) => s + r.rating, 0) / arr.length) * 100) / 100
    : 0;

  await supabase
    .from('profiles')
    .update({
      overall_rating_avg: avg(allRatings),
      ia_rating_avg:      avg(iaRatings),
      pa_rating_avg:      avg(paRatings),
      rating_count:       allRatings.length,
    })
    .eq('id', profileId);
};

// ─── NOMINATE MODAL ──────────────────────────────────────────────────────────
export const NominateModal = ({ currentUserId, onClose, onNominated }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [claimNumber, setClaimNumber] = useState('');
  const [requiresClaim, setRequiresClaim] = useState(false);

  // Check on mount if this user is already in rate-limit territory
  useEffect(() => {
    if (!currentUserId) return;
    const checkFlag = async () => {
      const { data } = await supabase
        .from('nomination_flags')
        .select('require_claim_num, nomination_count, window_start')
        .eq('user_id', currentUserId)
        .maybeSingle();

      if (!data) return;

      // Reset window if older than 24 hours
      const windowStart = new Date(data.window_start);
      const now = new Date();
      const hoursDiff = (now - windowStart) / (1000 * 60 * 60);

      if (hoursDiff >= 24) {
        // Window expired — reset the flag
        await supabase
          .from('nomination_flags')
          .update({ nomination_count: 0, window_start: now.toISOString(), require_claim_num: false })
          .eq('user_id', currentUserId);
      } else if (data.require_claim_num) {
        setRequiresClaim(true);
      }
    };
    checkFlag();
  }, [currentUserId]);

  const handleSubmit = async () => {
    setError('');

    if (!form.firstName.trim()) { setError('First name is required.'); return; }
    if (!form.lastName.trim())  { setError('Last name is required.');  return; }
    if (!form.email.trim())     { setError('Email is required.');       return; }
    if (requiresClaim && !claimNumber.trim()) {
      setError('Please enter a claim number to continue.');
      return;
    }


    // Simple email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setSaving(true);

     try {
      const { data, error: invokeError } = await supabase.functions.invoke('nominate-umpire', {
        body: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          claimNumber: claimNumber.trim() || null,
        },
      });

      // Supabase hides the response body on non-2xx unless we pull it
      // from the error's context manually
      let body = data;
      if (invokeError && invokeError.context) {
        try { body = await invokeError.context.json(); } catch { /* fall through */ }
      }

      if (body?.error_code === 'duplicate_profile' || body?.error_code === 'duplicate_user') {
        // Duplicate — abort the form
        setError(body.error);
        setSaving(false);
        return;
      }

      if (body?.error_code === 'invalid_email' || body?.error_code === 'missing_fields') {
        // Bad input — stay on the form so they can fix it
        setError(body.error);
        setSaving(false);
        return;
      }

      if (body?.error || invokeError) {
        // Unexpected — admin already alerted server-side
        setError(body?.error || 'Something went wrong. Please try again.');
        setSaving(false);
        return;
      }

      if (data?.requireClaim) setRequiresClaim(true);

      setSuccess(true);
      setTimeout(() => {
        onNominated();
        onClose();
      }, 2500);

    } catch (err) {
      console.error('Nomination error:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }}
    >
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '440px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ color: 'var(--navy)', margin: 0, fontSize: '18px' }}>Nominate an Umpire</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}
          >✕</button>
        </div>

        <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 }}>
          Know an umpire who isn't on UmpireIQ yet? Add them below. They'll receive an 
          email invitation to claim their profile.
        </p>

        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <p style={{ color: 'var(--navy)', fontWeight: 600, marginBottom: '8px' }}>
              Nomination sent!
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '13px' }}>
              {form.firstName} {form.lastName} has been added to UmpireIQ and will 
              receive an email invitation.
            </p>
          </div>
        ) : (
          <>
            {/* First Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                First Name <span style={{ color: 'var(--gold)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                placeholder="Jane"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', background: 'var(--cream)',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--navy)', fontSize: '14px',
                }}
              />
            </div>

            {/* Last Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Last Name <span style={{ color: 'var(--gold)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                placeholder="Smith"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', background: 'var(--cream)',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--navy)', fontSize: '14px',
                }}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: requiresClaim ? '16px' : '24px' }}>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Email <span style={{ color: 'var(--gold)' }}>*</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane.smith@example.com"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', background: 'var(--cream)',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--navy)', fontSize: '14px',
                }}
              />
            </div>

            {/* Claim number — only shows after 15 nominations in 24hrs */}
            {requiresClaim && (
              <div style={{ marginBottom: '24px', padding: '12px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px' }}>
                <p style={{ color: 'var(--gold)', fontSize: '12px', marginBottom: '10px', lineHeight: 1.5 }}>
                  You've nominated a high number of umpires in the last 24 hours. Please enter a 
                  claim number associated with this assignment to continue. This requirement resets 
                  automatically after 24 hours.
                </p>
                <input
                  type="text"
                  value={claimNumber}
                  onChange={e => setClaimNumber(e.target.value)}
                  placeholder="Claim number"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', background: 'var(--cream)',
                    border: '1px solid var(--border)', borderRadius: '6px',
                    color: 'var(--navy)', fontSize: '14px',
                  }}
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <p style={{ color: '#e05555', fontSize: '13px', marginBottom: '16px', lineHeight: 1.4 }}>
                {error}
              </p>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px', background: 'transparent',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--muted)', cursor: 'pointer', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  flex: 2, padding: '10px', background: saving ? 'var(--border)' : 'var(--gold)',
                  border: 'none', borderRadius: '6px',
                  color: saving ? 'var(--muted)' : 'var(--navy)',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: 600,
                }}
              >
                {saving ? 'Submitting…' : 'Send Nomination'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RateModal;
